/**
 * OneDial Africa — Analytics & error monitoring scaffold
 *
 * - Reads env vars at build time (REACT_APP_*).
 * - Only fires once the user has accepted the relevant cookie category.
 * - All hooks are safe no-ops when env vars are missing — perfect for
 *   the preview environment where we don't burn analytics quota.
 */

const ENV = process.env.REACT_APP_ENV || "preview";
const GA4_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID || "";
const GTM_ID = process.env.REACT_APP_GTM_CONTAINER_ID || "";
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN || "";

let _consent = readConsent();
let _gaLoaded = false;
let _gtmLoaded = false;
let _sentryLoaded = false;

function readConsent() {
  try {
    const raw = window.localStorage.getItem("onedial_cookie_consent");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function initAnalytics() {
  if (typeof window === "undefined") return;
  window.addEventListener("onedial:cookie-consent", (e) => {
    _consent = e.detail;
    maybeLoadAll();
  });
  maybeLoadAll();
}

function maybeLoadAll() {
  if (_consent?.analytics) {
    loadGA4();
    loadGTM();
  }
  // Sentry always loads (error monitoring, no user-tracking PII).
  loadSentry();
}

/* ── Google Analytics 4 ── */
function loadGA4() {
  if (_gaLoaded || !GA4_ID) return;
  _gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", GA4_ID, { anonymize_ip: true, send_page_view: true });
}

/* ── Google Tag Manager ── */
function loadGTM() {
  if (_gtmLoaded || !GTM_ID) return;
  _gtmLoaded = true;
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
}

/* ── Sentry (lightweight CDN loader, no SDK bundling) ── */
function loadSentry() {
  if (_sentryLoaded || !SENTRY_DSN) return;
  _sentryLoaded = true;
  const s = document.createElement("script");
  s.src = "https://browser.sentry-cdn.com/7.105.0/bundle.tracing.min.js";
  s.crossOrigin = "anonymous";
  s.async = true;
  s.onload = () => {
    try {
      if (window.Sentry) {
        window.Sentry.init({
          dsn: SENTRY_DSN,
          environment: ENV,
          tracesSampleRate: 0.1,
          replaysSessionSampleRate: 0,
        });
      }
    } catch (err) {
      // Never break the app if analytics fails to bootstrap (consent denial,
      // adblocker, network error).
      if (process.env.NODE_ENV !== "production") console.warn("analytics init", err?.message);
    }
  };
  document.head.appendChild(s);
}

/* ── Public event helper ── */
export function track(event, params = {}) {
  if (typeof window === "undefined") return;
  if (_consent?.analytics && window.gtag) {
    try { window.gtag("event", event, params); }
    catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("gtag event", err?.message);
    }
  }
  // Always available to GTM dataLayer (custom triggers can fire even without GA4)
  if (window.dataLayer && _consent?.analytics) {
    window.dataLayer.push({ event, ...params });
  }
}
